import { styled } from "@linaria/react";
import React from "react";
import { color, zIndex } from "..";
import useEnsuredForwardedRef from "../lib/hooks/useEnsuredForwardedRef";
import cn from "classnames";
import useHeaderHeight, {
  BANNER_HEIGHT_CSS_VARIABLE,
  PRIMARY_HEADER_HEIGHT_CSS_VARIABLE,
} from "../Header/useHeaderHeight";
import {
  AlertLine,
  CheckboxCircleLine,
  CloseLine,
  ErrorWarningLine,
  InformationLine,
} from "../IconV2/react";
import { colors, fonts } from "../Theme";
import Text from "../Text";
import { css } from "@linaria/core";

type AlertType = "warning" | "error" | "incomplete" | "valid";

const CSS_CLASS_CLOSING = "is-closing";
const CSS_CLASS_WARNING = "is-warning";
const CSS_CLASS_ERROR = "is-error";
const CSS_CLASS_INCOMPLETE = "is-incomplete";
const CSS_CLASS_SUCCESS = "is-success";
const CSS_CLASS_VALID = "is-valid";

interface P extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  icon?: React.Component;
  uiType: AlertType;
  hasIcon?: boolean;
  title?: string;
  onClose: () => void;
}

const StyledBanner = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 18px 16px;
  position: sticky;
  top: var(${PRIMARY_HEADER_HEIGHT_CSS_VARIABLE});
  z-index: ${zIndex.fixedSubheader};
  gap: 5px;
  /* align-items: center; */

  &.${CSS_CLASS_WARNING} {
    background-color: ${color.warningBackground};
  }

  &.${CSS_CLASS_ERROR} {
    background-color: ${color.errorBackground};
  }

  &.${CSS_CLASS_INCOMPLETE} {
    background-color: ${color.titanium100};
  }

  &.${CSS_CLASS_VALID}, &.${CSS_CLASS_SUCCESS} {
    background-color: ${color.successBackground};
  }

  transition: 200ms ease;
  transition-property: opacity, transform;

  ~ * {
    transition: transform 200ms ease;
  }

  &.${CSS_CLASS_CLOSING} {
    /* transform: translateY(-100%); */
    opacity: 0;

    ~ * {
      transition: opacity 200ms ease;
      /* transform: translateY(calc(var(${BANNER_HEIGHT_CSS_VARIABLE}) * -1)); */
    }
  }

  .wrapper {
    max-width: 1200px;
    margin-inline: auto;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    gap: 8px;

    > :first-child {
      flex-shrink: 0;
    }

    div {
      flex-grow: 1;
      max-width: 100%;
      color: ${colors.carbon800};
      margin-top: 2px;
      font: ${fonts.body1};
    }
  }
`;

const BannerTitle = css`
  margin-right: 8px;
  color: ${colors.carbon900};
`;

const BannerCloseButton = css`
  all: unset;
  cursor: pointer;
  position: absolute;
  top: 11px;
  right: 16px;
  color: ${colors.titanium500};
`;

function getBannerIcon(type: AlertType) {
  switch (type) {
    case "warning":
      return <AlertLine size="l" color={color.warning} />;
    case "error":
      return <ErrorWarningLine size="l" color={color.error} />;
    case "valid":
      return <CheckboxCircleLine size="l" color={color.success} />;
    case "incomplete":
      return <InformationLine size="l" color={color.titanium600} />;
    default: {
      const n: never = type;
      throw n;
    }
  }
}

const Banner = (
  { children, uiType, onClose, className, hasIcon = true, title, ...rest }: P,
  forwardedRef: React.Ref<HTMLDivElement>,
) => {
  const Icon = getBannerIcon(uiType);
  const bannerRef = useEnsuredForwardedRef(forwardedRef);

  useHeaderHeight(bannerRef, BANNER_HEIGHT_CSS_VARIABLE);

  const classNames = cn([
    className,
    {
      [CSS_CLASS_WARNING]: uiType === "warning",
      [CSS_CLASS_ERROR]: uiType === "error",
      [CSS_CLASS_VALID]: uiType === "valid",
      [CSS_CLASS_INCOMPLETE]: uiType === "incomplete",
    },
  ]);

  return (
    <StyledBanner ref={bannerRef} {...rest} className={classNames}>
      <div className="wrapper">
        {hasIcon ? Icon : null}
        <div>
          {" "}
          {title && title.length > 0 && (
            <Text as="span" type="body1SemiBold" className={BannerTitle}>
              {title}
            </Text>
          )}
          {children}
        </div>
      </div>
      <button type="button" onClick={onClose} className={BannerCloseButton}>
        <CloseLine size="l" />
      </button>
    </StyledBanner>
  );
};

export const useAnimateCloseBanner = (
  bannerRef: React.RefObject<HTMLElement>,
  hideFn: () => void,
) =>
  React.useCallback(() => {
    const BannerElement = bannerRef.current;
    BannerElement?.classList.add(CSS_CLASS_CLOSING);

    BannerElement?.addEventListener("transitionend", hideFn);
  }, [hideFn, bannerRef]);

export default React.forwardRef(Banner);
